import { useState, useEffect } from "react";
import { ArticleFormData } from "@/types/article";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface UseArticleFormProps {
  id?: string;
  isSpecial?: boolean;
  isEditMode: boolean;
  initialData?: ArticleFormData | null;
}

export const useArticleForm = ({ id, isSpecial, isEditMode, initialData }: UseArticleFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<ArticleFormData>({
    title: "",
    category: "",
    excerpt: "",
    content: "",
    image_url: "",
    sub_category: "",
    location: "",
    event_date: "",
    writer: ""
  });

  useEffect(() => {
    console.log("Initial data received:", initialData);
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        category: initialData.category || "",
        excerpt: initialData.excerpt || "",
        content: initialData.content || "",
        image_url: initialData.image_url || "",
        sub_category: initialData.sub_category || "",
        location: initialData.location || "",
        event_date: initialData.event_date || "",
        writer: initialData.writer || "",
      });
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const table = isSpecial ? "special_articles" : "articles";
      const submitData = isSpecial
        ? { ...formData, slug: id || formData.title.toLowerCase().replace(/\s+/g, "-") }
        : formData;

      console.log("Submitting data to table:", table, "Data:", submitData);
      console.log("Using identifier:", isSpecial ? "slug" : "id", "Value:", id);

      if (isEditMode) {
        const { error } = await supabase
          .from(table)
          .update(submitData)
          .eq("id", id); // 常にIDを使用して更新

        if (error) {
          console.error("Update error:", error);
          throw error;
        }

        toast({
          title: "記事を更新しました",
          description: "記事の更新に成功しました。",
        });
      } else {
        const { error } = await supabase.from(table).insert([submitData]);

        if (error) {
          console.error("Insert error:", error);
          throw error;
        }

        toast({
          title: "記事を投稿しました",
          description: "記事の投稿に成功しました。",
        });
      }
      navigate("/admin");
    } catch (error) {
      console.error("Error creating/updating article:", error);
      toast({
        variant: "destructive",
        title: "エラーが発生しました",
        description: isEditMode ? "記事の更新に失敗しました。" : "記事の投稿に失敗しました。",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    isLoading,
    handleSubmit,
  };
};